/**
 * Mover una orden de estado.
 *
 * POR QUE HAY UN PASO DE CONFIRMACION PARA UN CAMBIO DE ESTADO
 * -----------------------------------------------------------
 * Porque en el taller un cambio de estado NO es un cambio de estado: es lo que
 * dispara los efectos de `cambiar_estado_ot()`. Pasar a «En reparación» saca los
 * repuestos del stock; «Entregar» calcula la garantía y borra la clave del
 * equipo; «Anular» repone repuestos. Nada de eso se deshace con un Ctrl+Z, y
 * ninguna de las tres cosas se adivina desde el nombre del boton.
 *
 * Asi que el dialogo dice, siempre, QUE va a pasar ademas del cambio de estado.
 *
 * El caso de PRESUPUESTADO es distinto: ahi el dialogo ademas PIDE datos. La
 * base rechaza el cambio sin importe («Para presupuestar hay que informar el
 * importe.») y la constraint `presupuesto_requiere_moneda` no acepta un importe
 * sin moneda. Se piden los dos aca en vez de descubrirlo con un error rojo.
 */
import { useEffect, useState } from "react";
import { ArrowRight, KeyRound, Package, TriangleAlert } from "lucide-react";
import type { Moneda } from "@tora/shared";

import { InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type { EstadoOt } from "@/lib/tipos";

import {
  ACCION_ESTADO,
  ETIQUETA_ESTADO,
  datosDelCambio,
  efectoDe,
  nombreCliente,
  nombreOrden,
  pideImporte,
  validarPresupuesto,
  type OrdenTaller,
} from "./logica";

export interface DialogEstadoProps {
  orden: OrdenTaller | null;
  destino: EstadoOt | null;
  onCerrar: () => void;
  guardando?: boolean;
  /** Para que el selector de moneda del presupuesto convierta, y no solo etiquete. */
  cotizacion?: number;
  onConfirmar: (datos: Record<string, unknown>) => void;
}

export function DialogEstado({
  orden,
  destino,
  onCerrar,
  guardando = false,
  cotizacion,
  onConfirmar,
}: DialogEstadoProps): JSX.Element {
  const [importe, setImporte] = useState<number | null>(null);
  const [moneda, setMoneda] = useState<Moneda>("ARS");
  const [diagnostico, setDiagnostico] = useState("");
  const [observaciones, setObservaciones] = useState("");

  const ordenId = orden?.id ?? null;
  const monedaInicial = orden?.presupuesto_moneda ?? orden?.mano_obra_moneda ?? "ARS";
  const diagnosticoInicial = orden?.diagnostico ?? "";

  // Al abrir se arranca limpio, salvo la moneda y el diagnostico, que se
  // precargan con lo que la orden ya tiene: presupuestar suele ser confirmar lo
  // que se venia hablando, no empezar de cero.
  //
  // Las dependencias son el ID y el destino, no el objeto `orden`: la lista se
  // refresca sola cada tanto y con el objeto entero como dependencia, un refetch
  // en segundo plano borraria el importe que la persona esta tipeando.
  useEffect(() => {
    if (ordenId === null || !destino) return;
    setImporte(null);
    setMoneda(monedaInicial);
    setDiagnostico(diagnosticoInicial);
    setObservaciones("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordenId, destino]);

  const abierto = orden !== null && destino !== null;
  const accion = destino ? ACCION_ESTADO[destino] : null;
  const conImporte = destino !== null && pideImporte(destino);
  const presupuesto = validarPresupuesto({ importe, moneda });
  const listo = !conImporte || presupuesto.ok;

  function confirmar(): void {
    if (!orden || !destino || !listo) return;
    onConfirmar(
      datosDelCambio(destino, {
        presupuesto: conImporte ? { importe, moneda, diagnostico } : undefined,
        observaciones,
      }),
    );
  }

  return (
    <Dialog open={abierto} onOpenChange={(a) => (a ? undefined : onCerrar())}>
      <DialogContent tamano="md">
        <DialogHeader>
          <DialogTitle>{accion?.verbo ?? "Cambiar estado"}</DialogTitle>
          <DialogDescription>
            {orden ? `${nombreOrden(orden)} · ${orden.equipo} · ${nombreCliente(orden)}` : ""}
          </DialogDescription>
        </DialogHeader>

        {orden && destino && accion ? (
          <DialogBody>
            <Card variant="muted" padding="normal" className="space-y-2">
              <p className="flex flex-wrap items-center gap-1 text-sm">
                <span className="text-muted-foreground">{ETIQUETA_ESTADO[orden.estado]}</span>
                <ArrowRight className="size-4 text-muted-foreground" aria-hidden="true" />
                <span className="font-medium">{ETIQUETA_ESTADO[destino]}</span>
              </p>
              <p className="text-sm text-muted-foreground">{accion.resumen}</p>
            </Card>

            <Efecto destino={destino} orden={orden} />

            {conImporte ? (
              <>
                <InputImporte
                  label="Importe del presupuesto"
                  valor={importe}
                  onValorChange={setImporte}
                  moneda={moneda}
                  onMonedaChange={setMoneda}
                  cotizacion={cotizacion}
                  requerido
                  autoFocus
                  ayuda="Es el número que se le pasa al cliente. La base no guarda un presupuesto sin importe y moneda."
                />

                <Campo
                  label="Diagnóstico"
                  ayuda="Es lo que justifica el número de arriba cuando el cliente pregunte por qué sale eso."
                >
                  <Textarea
                    id="ot-diagnostico"
                    rows={2}
                    value={diagnostico}
                    onChange={(e) => setDiagnostico(e.target.value)}
                    placeholder="Qué se encontró: pantalla rota, batería agotada, humedad…"
                  />
                </Campo>
              </>
            ) : null}

            <Campo
              label="Nota (opcional)"
              ayuda="Se agrega a las observaciones que la orden ya tenga; no las reemplaza."
            >
              <Textarea
                id="ot-observaciones"
                rows={2}
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Lo que haya que dejar anotado de este paso"
              />
            </Campo>

            {conImporte && !presupuesto.ok && importe !== null ? (
              <Aviso tono="destructive">{presupuesto.problema}</Aviso>
            ) : null}
          </DialogBody>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant={accion?.delicada ? "destructive" : "default"}
            cargando={guardando}
            disabled={!listo}
            onClick={confirmar}
          >
            {`Confirmar: ${accion?.verbo ?? ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * El efecto lateral de esta transicion, con el peso visual que le corresponde.
 *
 * Los tres que mueven algo irreversible (stock, garantia, clave) van en un
 * recuadro marcado; el resto va como una linea de texto. Poner los ocho con el
 * mismo triangulo amarillo haria que el triangulo no signifique nada.
 */
function Efecto({ destino, orden }: { destino: EstadoOt; orden: OrdenTaller | null }): JSX.Element {
  const accion = ACCION_ESTADO[destino];
  // El texto de la orden y no el del estado: el cierre dice si el diagnostico
  // se cobra ahora o ya estaba pago.
  const efecto = efectoDe(destino, orden);

  if (!accion.delicada) {
    return <p className="text-sm text-muted-foreground">{efecto}</p>;
  }

  const Icono =
    destino === "EN_REPARACION" ? Package : destino === "ENTREGADO" ? KeyRound : TriangleAlert;

  return (
    <Aviso tono="warning" titulo="Esto además hace lo siguiente" icono={Icono}>
      {efecto}
    </Aviso>
  );
}
